import { createFileRoute, Link } from "@tanstack/react-router";
import { PropertyListView } from "@/components/public/PropertyListView";
import { useI18n } from "@/lib/i18n";
import { fetchPublicProperties } from "@/lib/publicData";
import { buildHead, listingCopy, pickLang, IN_LANGUAGE, SITE_URL } from "@/lib/seo";

const typeSlugToKey: Record<string, string> = {
  casas: "casa",
  departamentos: "departamento",
  terrenos: "terreno",
  oficinas: "oficina",
  locales: "local",
  duplex: "duplex",
};

export const Route = createFileRoute("/_public/$operation/$type/")({
  component: ListingPage,
  loader: ({ params }) =>
    fetchPublicProperties({
      operation: params.operation,
      property_type: typeSlugToKey[params.type] ?? params.type,
    }),
  head: ({ params, match }) => {
    const lang = pickLang(match.search);
    const copy = listingCopy(lang, params.operation, params.type);
    const path = `/${params.operation}/${params.type}`;
    const { meta, links, url } = buildHead({
      lang,
      path,
      title: copy.title,
      description: copy.description,
    });
    return {
      meta,
      links,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: copy.title,
            description: copy.description,
            url,
            inLanguage: IN_LANGUAGE[lang],
            isPartOf: {
              "@type": "WebSite",
              name: "Dahiana Marín Bienes Raíces",
              url: SITE_URL,
            },
          }),
        },
      ],
    };
  },
});

function ListingPage() {
  const { operation, type } = Route.useParams();
  const initial = Route.useLoaderData();
  const { lang } = useI18n();
  const copy = listingCopy(lang, operation, type);
  const typeKey = typeSlugToKey[type] ?? type;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <nav className="text-xs uppercase tracking-wider text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          {lang === "en" ? "Home" : lang === "pt" ? "Início" : "Inicio"}
        </Link>
        <span className="mx-2">·</span>
        <span>{copy.h1}</span>
      </nav>
      <h1 className="mt-3 text-3xl font-semibold text-foreground md:text-4xl">{copy.h1}</h1>
      <p className="mt-2 text-sm text-muted-foreground md:text-base">{copy.intro}</p>

      <div className="mt-10">
        <PropertyListView filters={{ operation, property_type: typeKey }} initialProperties={initial as never} />
      </div>
    </div>
  );
}
